import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { connect } from "react-redux";
import {
  Button,
  Container,
  Card,
  Modal,
  Dropdown,
  Grid,
  Form,
} from "semantic-ui-react";
import JobPostingQuestionLabel from "./JobPostingQuestionLabel";
import {
  getSuccessToastWithMessage,
  getFailureToastWithMessage,
} from "../../shared/toast/ToastOptions";
import { ADD_JOB_POSTING_MUTATION } from "../../../services/graphql/mutations/EmployerMutations";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { EditorState, convertToRaw } from "draft-js";
import { draftToMarkdown } from "markdown-draft-js";
import { jobPostingDescriptionToolbar } from "./jobPostingDescriptionToolbar";

const CreateJobPostingModal = (props) => {
  const [addJobPosting, { mutationData }] = useMutation(
    ADD_JOB_POSTING_MUTATION
  );

  const [jobPostingGeneralInfo, setJobPostingGeneralInfo] = useState({});
  const [jobPostingLocation, setJobPostingLocation] = useState({});
  const [
    jobPostingFieldIdsMappedToRequiredFlag,
    setJobPostingFieldIdsMappedToRequiredFlag,
  ] = useState({});
  const [jobPostingDescription, setJobPostingDescription] = useState(() =>
    EditorState.createEmpty()
  );

  const handleJobPostingChange = (event, setJobPostingInfo, jobPostingInfo) => {
    setJobPostingInfo({
      ...jobPostingInfo,
      [event.target.name]: event.target.value,
    });
  };

  const jobPostingFieldOptions = () => {
    return props.jobPostingFields.map((field) => {
      return {
        key: field.id,
        value: field.id,
        text: field.value,
      };
    });
  };

  const addJobPostingField = (event, data) => {
    const currentJobPostingFields = {};
    data["value"].forEach((fieldId) => {
      if (!(fieldId in jobPostingFieldIdsMappedToRequiredFlag)) {
        currentJobPostingFields[fieldId] = false;
      } else {
        currentJobPostingFields[fieldId] =
          jobPostingFieldIdsMappedToRequiredFlag[fieldId];
      }
    });
    setJobPostingFieldIdsMappedToRequiredFlag(currentJobPostingFields);
  };

  const makeJobPostingFieldRequired = (fieldId) => {
    const currentJobPostingFieldIdsMappedToRequiredFlag = {
      ...jobPostingFieldIdsMappedToRequiredFlag,
    };
    if (currentJobPostingFieldIdsMappedToRequiredFlag[fieldId]) {
      currentJobPostingFieldIdsMappedToRequiredFlag[fieldId] = false;
    } else {
      currentJobPostingFieldIdsMappedToRequiredFlag[fieldId] = true;
    }
    setJobPostingFieldIdsMappedToRequiredFlag(
      currentJobPostingFieldIdsMappedToRequiredFlag
    );
  };

  const handleSubmitButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const newJobPostingInput = {
      ...jobPostingGeneralInfo,
      employerId: props.employer.employerId,
      location: jobPostingLocation,
      description: draftToMarkdown(
        convertToRaw(jobPostingDescription.getCurrentContent())
      ),
      fields: formatJobPostingFields(),
    };

    addJobPosting({
      variables: {
        jobPostingInput: newJobPostingInput,
      },
    })
      .then(({ data }) => {
        props.addJobPosting(data.addJobPosting.jobPosting);
        props.setShowCreateJobPostingModal(false);
        props.openToast(
          getSuccessToastWithMessage("Successfully created job posting!")
        );
      })
      .catch((error) => {
        console.log(error);
        props.setShowCreateJobPostingModal(false);
        props.openToast(
          getFailureToastWithMessage(
            "Failed to created job posting, please try again."
          )
        );
      });
  };

  const formatJobPostingFields = () => {
    return Object.keys(jobPostingFieldIdsMappedToRequiredFlag).map((id) => {
      return { id, required: jobPostingFieldIdsMappedToRequiredFlag[id] };
    });
  };

  const renderItemContent = (item) => {
    return (
      <JobPostingQuestionLabel
        questionTitle={item.text}
        value={item.value}
        checked={!!jobPostingFieldIdsMappedToRequiredFlag[item.value]}
        removeJobPostingField={removeJobPostingField}
        makeJobPostingFieldRequired={makeJobPostingFieldRequired}
      />
    );
  };

  const removeJobPostingField = (fieldId) => {
    const currentJobPostingFieldIdsMappedToRequiredFlag = {
      ...jobPostingFieldIdsMappedToRequiredFlag,
    };
    delete currentJobPostingFieldIdsMappedToRequiredFlag[fieldId];
    setJobPostingFieldIdsMappedToRequiredFlag(
      currentJobPostingFieldIdsMappedToRequiredFlag
    );
  };

  return (
    <Modal
      onClose={() => props.setShowCreateJobPostingModal(false)}
      onOpen={() => props.setShowCreateJobPostingModal(true)}
      open={props.showCreateJobPostingModal}
      closeIcon="cancel"
    >
      <Modal.Header>Create Job Posting</Modal.Header>
      <Modal.Content>
        <Form
          onSubmit={(event) => {
            handleSubmitButtonClick(event);
          }}
        >
          <Card fluid>
            <Card.Content>
              <Card.Header>Location</Card.Header>
              <Form.Group widths="equal">
                <Form.Field
                  required
                  name="city"
                  label="City"
                  control="input"
                  value={jobPostingLocation.city}
                  onChange={(event) => {
                    handleJobPostingChange(
                      event,
                      setJobPostingLocation,
                      jobPostingLocation
                    );
                  }}
                  placeholder="Enter a City"
                />
                <Form.Field
                  required
                  name="state"
                  label="State"
                  control="input"
                  value={jobPostingLocation.state}
                  onChange={(event) => {
                    handleJobPostingChange(
                      event,
                      setJobPostingLocation,
                      jobPostingLocation
                    );
                  }}
                  placeholder="Enter a State"
                />
                <Form.Field
                  label="Zip Code"
                  name="zipCode"
                  required
                  control="input"
                  value={jobPostingLocation.zipCode}
                  onChange={(event) => {
                    handleJobPostingChange(
                      event,
                      setJobPostingLocation,
                      jobPostingLocation
                    );
                  }}
                  placeholder="Enter a Zip Code"
                />
              </Form.Group>
            </Card.Content>
          </Card>
          <Card fluid>
            <Card.Content>
              <Card.Header textAlign="left">General Info</Card.Header>
              <Grid>
                <Grid.Row columns={2}>
                  <Grid.Column>
                    <Form.Field
                      label="Job Role"
                      name="role"
                      required
                      control="input"
                      value={jobPostingGeneralInfo.role}
                      onChange={(event) => {
                        handleJobPostingChange(
                          event,
                          setJobPostingGeneralInfo,
                          jobPostingGeneralInfo
                        );
                      }}
                      placeholder="Enter a Job Role"
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field
                      label="Team"
                      name="team"
                      control="input"
                      value={jobPostingGeneralInfo.team}
                      onChange={(event) => {
                        handleJobPostingChange(
                          event,
                          setJobPostingGeneralInfo,
                          jobPostingGeneralInfo
                        );
                      }}
                      placeholder="Enter a Team"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={1}>
                  <Grid.Column>
                    <Card label="Job Description" fluid>
                      <Editor
                        editorState={jobPostingDescription}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        onEditorStateChange={setJobPostingDescription}
                        toolbar={jobPostingDescriptionToolbar}
                      />
                    </Card>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Card.Content>
          </Card>
          <Card fluid>
            <Card.Content>
              <Card.Header>Job Posting Questions</Card.Header>
              <Dropdown
                placeholder="Select Job Posting Questions"
                fluid
                search
                selection
                multiple
                value={Object.keys(jobPostingFieldIdsMappedToRequiredFlag).map(
                  Number
                )}
                renderLabel={renderItemContent}
                onChange={addJobPostingField}
                options={jobPostingFieldOptions()}
              />
            </Card.Content>
          </Card>
          <Container textAlign="center">
            <Button.Group attached="bottom">
              <Button positive>Submit</Button>
              <Button.Or />
              <Button
                negative
                onClick={() => {
                  props.setShowCreateJobPostingModal(false);
                }}
              >
                Cancel
              </Button>
            </Button.Group>
          </Container>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    openToast: (toast) => dispatch({ type: "OPEN_TOAST", payload: toast }),
    addJobPosting: (jobPosting) =>
      dispatch({ type: "ADD_JOB_POSTING", payload: jobPosting }),
  };
};

const mapStateToProps = (state) => {
  return {
    employer: state.employer,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateJobPostingModal);
